import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BsXCircle } from 'react-icons/bs';
import { useNavigate } from 'react-router';

import { ResponsiveModalBottomSheet } from '@/components';
import Button from '@/components/Button';
import ResponsiveModalPage from '@/components/ResponsiveModalPage';
import StickyFooter from '@/components/StickyFooter';
import type { BookingPaymentLocationState } from '@/models';
import BookingCostSummary from '@/pages/components/BookingCostSummary';
import { BookingService } from '@/services';

interface PromptPayPaymentModalPageProps {
  isOpen: boolean;
  onClose: () => void;
  bookingState?: BookingPaymentLocationState | null;
}

const DEFAULT_POLL_MS = Number(
  import.meta.env.VITE_PROMPTPAY_POLL_INTERVAL_MS || 5000,
);

const PromptPayPaymentModalPage: React.FC<PromptPayPaymentModalPageProps> = ({
  isOpen,
  onClose,
  bookingState,
}) => {
  const navigate = useNavigate();

  const bookingCode = bookingState?.bookingCode;
  const qrImg = bookingState?.paymentQrImageBase64;
  const qrExpiresAt = useMemo(
    () =>
      bookingState?.paymentQrExpiresAt
        ? new Date(bookingState.paymentQrExpiresAt)
        : null,
    [bookingState?.paymentQrExpiresAt],
  );

  const initialRemaining = qrExpiresAt
    ? Math.max(0, Math.floor((qrExpiresAt.getTime() - Date.now()) / 1000))
    : 0;
  const [remainingSeconds, setRemainingSeconds] =
    useState<number>(initialRemaining);
  const [showExpiredSheet, setShowExpiredSheet] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);

  const pollStatus = useCallback(async () => {
    if (!bookingCode) return;
    try {
      const { status: currentStatus } =
        await BookingService.getQrPaymentStatus(bookingCode);
      if (currentStatus === 'paid') {
        // Navigate to thank you page (bookingCode already stored in booking store)
        navigate('/booking/thank-you');
      } else if (currentStatus === 'expired') {
        setShowExpiredSheet(true);
      }
    } catch (e) {
      console.error('QR status polling failed', e);
    }
  }, [bookingCode, navigate]);

  useEffect(() => {
    if (!isOpen) return; // start only when open
    pollStatus();
    pollTimerRef.current = window.setInterval(pollStatus, DEFAULT_POLL_MS);
    return () => {
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
    };
  }, [isOpen, pollStatus]);

  useEffect(() => {
    if (!isOpen || !qrExpiresAt) return;
    countdownRef.current = window.setInterval(() => {
      const left = Math.max(
        0,
        Math.floor((qrExpiresAt.getTime() - Date.now()) / 1000),
      );
      setRemainingSeconds(left);
      if (left === 0) setShowExpiredSheet(true);
    }, 1000);
    return () => {
      if (countdownRef.current) window.clearInterval(countdownRef.current);
    };
  }, [isOpen, qrExpiresAt]);

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const { t } = useTranslation();

  return (
    <ResponsiveModalPage
      isOpen={isOpen}
      onClose={onClose}
      title="PromptPay"
      desktopWidthClass="md:w-2/3 2xl:w-1/2"
      //   desktopHeightClass="md:h-[85vh]"
    >
      <div className="flex w-full flex-col items-center gap-5 overflow-y-auto px-4 py-6 md:flex-row md:gap-6">
        <div className="flex flex-col items-center gap-5 md:w-1/2">
          {/* Countdown */}
          <div className="mt-2 flex w-full flex-col items-center text-center md:hidden">
            <p className="text-secondary-text w-full leading-6">
              {t('promptpay.payWithin')}
            </p>
            <p className="text-error w-full text-2xl leading-8 font-semibold">
              {formatCountdown(remainingSeconds)}
            </p>
          </div>
          <img
            src="/illustrations/promptpay-header.png"
            alt="PromptPay Header"
            className="w-full"
          />
          {qrImg && (
            <img
              src={
                qrImg.startsWith('data:')
                  ? qrImg
                  : `data:image/png;base64,${qrImg}`
              }
              alt="PromptPay QR"
              className="max-h-[280px] max-w-[280px]"
            />
          )}
          <div className="text-dark-text w-full text-center text-xl leading-6 font-medium">
            {t('promptpay.merchantName')}
          </div>
          <div className="text-secondary-text w-full text-center leading-6">
            <p className="mb-0">{t('promptpay.stayOnPage')}</p>
            <p>{t('promptpay.redirectNotice')}</p>
          </div>
        </div>
        <div className="hidden h-full flex-col justify-between gap-6 md:flex md:w-1/2">
          {/* Countdown */}
          <div className="mt-2 flex w-full flex-col items-center text-center">
            <p className="text-secondary-text w-full leading-6">
              {t('promptpay.payWithin')}
            </p>
            <p className="text-error w-full text-2xl leading-8 font-semibold">
              {formatCountdown(remainingSeconds)}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <hr className="border-t border-gray-200" />
            <BookingCostSummary totalLabel={t('promptpay.totalRemaining')} />
          </div>
        </div>
      </div>
      <StickyFooter className="md:hidden">
        <BookingCostSummary totalLabel={t('promptpay.totalRemaining')} />
      </StickyFooter>
      <ResponsiveModalBottomSheet
        isOpen={showExpiredSheet}
        onClose={() => {
          /* locked */
        }}
        heightClass="h-auto"
        closable={false}
        title={t('promptpay.paymentTitle')}
        stickyFooter={
          <div className="w-full p-4 pt-0">
            <Button
              className="tracking-tight"
              onClick={() => {
                setShowExpiredSheet(false);
                onClose();
              }}
            >
              {t('common.tryAgain')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-4 px-6 pt-2 pb-6 text-center">
          <BsXCircle className="text-error size-10" aria-hidden="true" />
          <div className="text-primary-text text-lg leading-7">
            {t('promptpay.expiredMessage')}
          </div>
        </div>
      </ResponsiveModalBottomSheet>
    </ResponsiveModalPage>
  );
};

export default PromptPayPaymentModalPage;
